import { pickT } from "@repo/shared";
import type { FlowField } from "@repo/shared/types";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { ApiError, del } from "@/lib/api/_client";
import { checkFieldDelete } from "@/lib/api/flow-editor";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useFlowFields } from "@/lib/hooks/use-flows";
import { DuplicateFlowFieldDialog } from "./duplicate-flow-field-dialog";
import { FlowFieldDialog } from "./flow-field-dialog";
import { T } from "./flow-fields-tab.i18n";

type FlowFieldsTabProps = {
  flowId: string;
  flowLanguage: string;
  mutate: () => void;
};

export function FlowFieldsTab({ flowId, flowLanguage, mutate }: FlowFieldsTabProps) {
  const t = pickT(T, flowLanguage);
  const { fields } = useFlowFields(flowId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FlowField | null>(null);
  const [duplicatingField, setDuplicatingField] = useState<FlowField | null>(null);
  const [deletingField, setDeletingField] = useState<FlowField | null>(null);
  const [loading, setLoading] = useState(false);
  const { confirm, ConfirmationDialog } = useConfirm();

  const handleCreate = () => {
    setEditingField(null);
    setDialogOpen(true);
  };

  const handleEdit = (field: FlowField) => {
    setEditingField(field);
    setDialogOpen(true);
  };

  const showDeleteError = (message: string) =>
    confirm({
      title: t.error,
      description: message,
      confirmText: t.ok,
      variant: "destructive",
      type: "alert",
    });

  // QL-168 — a field the server would refuse to delete shows why straight away;
  // the confirmation only opens when the delete is allowed.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: (fieldId) => checkFieldDelete(flowId, fieldId),
    onAllowed: (fieldId) => setDeletingField(fields.find((f) => f.id === fieldId) ?? null),
    onBlocked: (reason) => showDeleteError(reason || t.failedToDeleteField),
  });

  const confirmDelete = async () => {
    if (!deletingField) return;

    setLoading(true);
    try {
      await del(`/internal/flows/${flowId}/fields/${deletingField.id}`);
      mutate();
      setDeletingField(null);
    } catch (err) {
      await showDeleteError(err instanceof ApiError ? err.message : t.failedToDeleteField);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = (success: boolean) => {
    setDialogOpen(false);
    setEditingField(null);
    if (success) {
      mutate();
    }
  };

  const handleDuplicateClose = (success: boolean) => {
    setDuplicatingField(null);
    if (success) {
      mutate();
    }
  };

  const getFieldTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      text: t.text,
      number: t.number,
      dropdown: t.dropdown,
      textarea: t.textArea,
      date: t.date,
      datetime: t.datetime,
      email: t.email,
      phone: t.phone,
    };
    return labels[type] || type;
  };

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center gap-4">
          <p className="text-muted-foreground">{t.fieldsDescription}</p>
          <Button onClick={handleCreate}>{t.addField}</Button>
        </div>

        {fields.length === 0 ? (
          <EmptyState action={<Button onClick={handleCreate}>{t.addField}</Button>}>
            {t.noFieldsYet}
          </EmptyState>
        ) : (
          <Table variant="data">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.type}</TableHead>
                <TableHead>{t.scope}</TableHead>
                <TableHead>{t.syncKey}</TableHead>
                <TableHead>{t.required}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field) => (
                <TableRow key={field.id}>
                  <TableCell className="font-medium">{field.name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getFieldTypeLabel(field.type)}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {field.scope === "portfolio"
                        ? t.portfolio
                        : field.scope === "goal"
                          ? t.goal
                          : t.global}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {field.syncKey && (
                      <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                        {field.syncKey}
                      </code>
                    )}
                  </TableCell>
                  <TableCell>
                    {field.isRequired ? (
                      <Badge variant="default">{t.required}</Badge>
                    ) : (
                      <Badge variant="outline">{t.optional}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(field)}
                        tooltip={t.editField}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDuplicatingField(field)}
                        tooltip={t.duplicateField}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => requestDelete(field.id)}
                        loading={checkingId === field.id}
                        disabled={loading}
                        tooltip={t.delete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <FlowFieldDialog
        flowId={flowId}
        flowLanguage={flowLanguage}
        field={editingField}
        fields={fields}
        open={dialogOpen}
        onClose={handleDialogClose}
      />

      <DuplicateFlowFieldDialog
        flowId={flowId}
        flowLanguage={flowLanguage}
        field={duplicatingField}
        onClose={handleDuplicateClose}
      />

      <AlertDialog open={!!deletingField} onOpenChange={(open) => !open && setDeletingField(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deleteField}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deleteFieldConfirmation(deletingField?.name || "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={loading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? t.deleting : t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConfirmationDialog />
    </>
  );
}
